import styled from '@emotion/styled';
import { Author, UUID } from '@myme/utils';
import { HeartIcon, PriceIcon, StarIcon } from 'src/icons';
import { mq } from 'src/themes';
import { usePalette, useTranslation } from 'src/hooks';
import { creatorProfilePageRoute, mymeDetailsPageRoute } from 'src/routes/route-list';
import { Link, Typography } from 'src/components/atoms';
import { Flex } from 'src/components/layout';
import { MymePreviewProgressBar } from './ProgressBar';
import { SingleIcon } from './SingleIcon';

interface IconsContent {
  likes: number;
  rating: number;
  price: number | null;
}
interface Props {
  id: UUID;
  name: string;
  author: Author;
  statIcons?: IconsContent;
  progress?: number;
  hideAuthorName?: boolean;
}

const StyledDetailsContainer = styled.div`
  grid-area: 'details';
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0.5rem;

  ${mq.sm} {
    h5 {
      font-size: 14px;
    }
  }

  ${mq.xs} {
    h5 {
      font-size: 12px;
      margin-bottom: 3px;
    }

    p,
    a {
      font-size: 11px;
    }
  }
`;

export const MymePreviewDetails = ({ id, name, author, progress, statIcons, hideAuthorName }: Props) => {
  const { t } = useTranslation();
  const { error, warning, success } = usePalette();

  return (
    <StyledDetailsContainer>
      <Typography variant='h5' sx={{ wordBreak: 'break-word', maxWidth: '20rem' }}>
        <Link to={mymeDetailsPageRoute(id)}>{name}</Link>
      </Typography>
      {!hideAuthorName && (
        <Typography variant='body2' fontSize={12}>
          {`${t('pages.feed.myme.author')} `}
          <Link to={creatorProfilePageRoute(author.id)}>{author.name}</Link>
        </Typography>
      )}
      {statIcons ? (
        <Flex mt={2}>
          <SingleIcon value={statIcons.likes} icon={<HeartIcon fill={error.main} />} />
          <SingleIcon value={statIcons.rating} icon={<StarIcon fill={warning.main} />} />
          {statIcons.price ? <SingleIcon value={statIcons.price} icon={<PriceIcon fill={success.main} />} /> : null}
        </Flex>
      ) : null}
      {progress !== undefined ? <MymePreviewProgressBar value={progress} /> : null}
    </StyledDetailsContainer>
  );
};
